/**
 * 🎣 OasisFishingPage — The Glass-Spring Oasis fishing loop.
 * Owner canon Mar 5, 2026.
 *
 * Zero combat. Cast with bait, reel up a fish (or a piece of canyon
 * trash), sell it to the Sun-Cured Tackle Shack — or hand a rag to
 * David / a clamshell to Cari.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useRoamZoneGate } from "@/hooks/useRoamZoneGate";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAST_ANIM_MS = 1200;
// 🎵 "Waiting at the Inlet" — owner-provided Oasis fishing theme (Mar 5, 2026)
const OASIS_THEME_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/lslzu2ia_waiting_at_the_inlet.mp4";
const MUSIC_PREF_KEY = "omni.oasis.music_on";

export default function OasisFishingPage() {
  const [data, setData] = useState(null);
  const [chits, setChits] = useState(0);
  const [casting, setCasting] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [selectedBait, setSelectedBait] = useState(null);
  const [tab, setTab] = useState("dock");   // dock · shack · bag
  const nav = useNavigate();
  const roamGate = useRoamZoneGate();

  // 🎵 Music state — persists across visits so the player's preference sticks.
  const audioRef = useRef(null);
  const [musicOn, setMusicOn] = useState(() => {
    try {
      const raw = localStorage.getItem(MUSIC_PREF_KEY);
      return raw === null ? true : raw === "1";
    } catch { return true; }
  });

  const refresh = useCallback(async () => {
    try {
      const [oasis, me] = await Promise.all([
        axios.get(`${API}/oasis/state`),
        axios.get(`${API}/auth/me`),
      ]);
      setData(oasis.data);
      setChits(me.data?.chits ?? 0);
      if (!selectedBait) {
        const first = Object.entries(oasis.data.state.bait).find(([, n]) => n > 0);
        if (first) setSelectedBait(first[0]);
      }
    } catch (e) {
      // silent
    }
  }, [selectedBait]);

  useEffect(() => { refresh(); }, [refresh]);

  // 🎵 Music side-effects — apply toggle to the audio element and persist.
  useEffect(() => {
    try { localStorage.setItem(MUSIC_PREF_KEY, musicOn ? "1" : "0"); } catch {}
    const a = audioRef.current;
    if (!a) return;
    if (musicOn) {
      a.volume = 0.55;
      a.play().catch(() => {
        // Autoplay blocked — the user has to tap the toggle explicitly.
      });
    } else {
      a.pause();
    }
  }, [musicOn]);

  // Cleanup on unmount — stop the audio when leaving the Oasis so the
  // theme doesn't bleed into other surfaces.
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        try { a.pause(); a.currentTime = 0; } catch {}
      }
    };
  }, []);

  const s = data?.state;
  const bait = data?.bait_catalog || {};
  const fish = data?.fish_catalog || {};
  const trash = data?.trash_catalog || {};

  const ownedBait = Object.entries(s?.bait || {}).filter(([, n]) => n > 0);
  const totalCaught = useMemo(() => {
    const fishSum = Object.values(s?.fish_bag || {}).reduce((a, b) => a + b, 0);
    const trashSum = Object.values(s?.trash_bag || {}).reduce((a, b) => a + b, 0);
    return { fish: fishSum, trash: trashSum };
  }, [s]);

  const cast = async () => {
    if (casting) return;
    setCasting(true);
    setReveal(null);
    try {
      const r = await axios.post(`${API}/oasis/cast`, {
        bait_id: selectedBait || null,
      });
      // Dramatic pause before the reveal
      setTimeout(() => {
        setReveal(r.data.catch);
        setCasting(false);
        refresh();
      }, CAST_ANIM_MS);
    } catch (e) {
      setCasting(false);
      toast.error(e?.response?.data?.detail || "Couldn't cast — try again.");
    }
  };

  const buyBait = async (id) => {
    const b = bait[id];
    const ok = await confirmDialog({
      title: `Buy ${b?.name || "bait"}?`,
      message: `${b?.icon || "🪱"} ${b?.name}
Cost: 🪙 ${b?.price_chits} Chits. ${b?.blurb || ""}`,
      confirmLabel: `Buy — ${b?.price_chits} 🪙`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/oasis/buy-bait`, { bait_id: id, count: 1 });
      toast.success(`Bought ${bait[id]?.name}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bait unavailable.");
    }
  };

  const sellFish = async (id) => {
    try {
      const r = await axios.post(`${API}/oasis/sell-fish`, { fish_id: id, count: 1 });
      toast.success(`+${r.data.chits_gained} chits`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sale rejected.");
    }
  };

  const sellTrash = async (id) => {
    try {
      const r = await axios.post(`${API}/oasis/sell-trash`, { trash_id: id, count: 1 });
      toast.success(`+${r.data.chits_gained} chit`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sale rejected.");
    }
  };

  const giftTrash = async (id) => {
    try {
      const r = await axios.post(`${API}/oasis/gift-trash`, { trash_id: id });
      toast.success(r.data.flavor || "Given.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not gift-able.");
    }
  };

  if (!s) {
    return (
      <div className="min-h-screen bg-black text-cyan-200 flex items-center justify-center" data-testid="oasis-loading">
        Loading Glass-Spring Oasis…
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-cyan-100 pb-24"
      style={{
        background:
          "linear-gradient(180deg, #052538 0%, #051f2d 45%, #0a2130 100%)",
      }}
      data-testid="oasis-fishing-page"
    >
      {/* 🎵 Oasis theme — "Waiting at the Inlet". Muted-by-default so browser
          autoplay policies never break the loop; the header toggle unmutes.
          Uses the mp4 as the audio source (browsers decode the audio track). */}
      <audio
        ref={audioRef}
        src={OASIS_THEME_URL}
        loop
        preload="auto"
        data-testid="oasis-theme-audio"
      />

      {/* Header */}
      <div className="border-b border-cyan-800/40 bg-black/50 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-cyan-400">
                🌊 The Glass-Spring Oasis
              </div>
              <h1
                className="text-3xl md:text-4xl font-black text-cyan-100 leading-tight mt-0.5"
                style={{ fontFamily: "Anton, sans-serif" }}
              >
                THE QUIET DROP
              </h1>
              <p className="text-[11px] text-cyan-200/60 italic mt-1">
                Zero combat. Zero timers. Just water, David's kettle, Cari's slab, and Roc watching from the rim.
              </p>
            </div>
            {/* 🎵 Music toggle — persists via localStorage */}
            <button
              type="button"
              onClick={() => setMusicOn((v) => !v)}
              data-testid="oasis-music-toggle"
              title={musicOn ? "Pause Waiting at the Inlet" : "Play Waiting at the Inlet"}
              className={`shrink-0 rounded-full w-11 h-11 flex items-center justify-center text-xl transition-all border ${
                musicOn
                  ? "bg-cyan-500/25 border-cyan-300 text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.5)]"
                  : "bg-black/60 border-cyan-900/50 text-cyan-500 hover:text-cyan-200"
              }`}
            >
              {musicOn ? "🎵" : "🔇"}
            </button>
          </div>
          <div className="flex items-center gap-3 mt-3 text-sm">
            <span className="px-2.5 py-1 rounded-full bg-yellow-600/20 border border-yellow-700/50 text-yellow-100">
              🪙 {chits} chits
            </span>
            <span className="px-2.5 py-1 rounded-full bg-cyan-900/40 border border-cyan-700/50">
              🐟 {totalCaught.fish} fish
            </span>
            <span className="px-2.5 py-1 rounded-full bg-neutral-800/60 border border-neutral-700">
              🗑️ {totalCaught.trash} trash
            </span>
          </div>

          {/* 🌾 Wander the Shore — side-mode entry to the Castle Courtyard roam zone */}
          <button
            type="button"
            onClick={() => roamGate.unlocked && nav("/roam")}
            disabled={!roamGate.unlocked}
            data-testid="oasis-roam-link"
            className={`mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              roamGate.unlocked
                ? "border-amber-400/60 text-amber-100 bg-amber-500/10 hover:bg-amber-500/20"
                : "border-neutral-800 text-neutral-500 bg-black/30 cursor-not-allowed"
            }`}
            title={roamGate.unlocked ? "Wander to the Castle Courtyard" : roamGate.reason}
          >
            <span>🚶</span>
            <span>{roamGate.unlocked ? "Wander the Shore → Courtyard" : `🔒 ${roamGate.reason}`}</span>
          </button>
        </div>
      </div>

      {/* Tab pills */}
      <div className="max-w-4xl mx-auto px-4 mt-4">
        <div className="flex gap-2">
          {[
            { k: "dock",  label: "🎣 Dock",  test: "tab-dock" },
            { k: "shack", label: "🏪 Shack", test: "tab-shack" },
            { k: "bag",   label: "🎒 Bag",   test: "tab-bag" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={t.test}
              className={`px-4 py-2 text-xs font-black uppercase tracking-widest rounded-md border ${
                tab === t.k
                  ? "bg-cyan-500/20 border-cyan-400 text-cyan-100"
                  : "bg-black/40 border-cyan-900/50 text-cyan-300/70 hover:text-cyan-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 mt-5">
        {tab === "dock" && (
          <div className="space-y-4">
            <div
              className="rounded-2xl border border-cyan-800/50 bg-gradient-to-br from-cyan-950/50 to-black p-6"
              data-testid="oasis-dock"
            >
              <div className="text-center">
                {casting && (
                  <div className="py-8 animate-pulse" data-testid="cast-anim">
                    <div className="text-5xl">🎣</div>
                    <div className="mt-2 text-cyan-300 text-sm italic">
                      Line's out… the water goes still…
                    </div>
                  </div>
                )}
                {!casting && reveal && (
                  <div className="py-6" data-testid="cast-reveal">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-cyan-400">
                      {reveal.kind === "fish" ? "You caught" : "You reeled up"}
                    </div>
                    <div className="text-6xl mt-2">{reveal.icon}</div>
                    <div className="text-xl font-black text-cyan-100 mt-2">
                      {reveal.name}
                    </div>
                    <div className="text-[11px] text-cyan-200/70 italic mt-1 max-w-md mx-auto">
                      {reveal.blurb}
                    </div>
                  </div>
                )}
                {!casting && !reveal && (
                  <div className="py-6">
                    <div className="text-5xl opacity-40">💤</div>
                    <div className="text-xs text-cyan-300/60 mt-2">
                      Pick a bait, then cast.
                    </div>
                  </div>
                )}
              </div>

              {/* Bait picker */}
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.35em] text-cyan-400 mb-2">
                  Bait
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.values(bait).map((b) => {
                    const owned = s.bait[b.id] || 0;
                    const isSelected = selectedBait === b.id;
                    return (
                      <button
                        key={b.id}
                        onClick={() => setSelectedBait(b.id)}
                        disabled={owned <= 0}
                        data-testid={`bait-pick-${b.id}`}
                        className={`text-left rounded-lg border p-3 transition-all ${
                          owned <= 0
                            ? "opacity-40 border-neutral-800 bg-black/40 cursor-not-allowed"
                            : isSelected
                              ? "border-cyan-300 bg-cyan-900/40"
                              : "border-cyan-800/50 bg-black/50 hover:border-cyan-500"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{b.icon}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-black text-cyan-100">
                              {b.name}
                            </div>
                            <div className="text-[10px] text-cyan-300/70">
                              Targets {b.targets.replace(/_/g, " ")}
                            </div>
                          </div>
                          <span className="text-xs font-mono text-cyan-300 shrink-0">
                            ×{owned}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setSelectedBait(null)}
                  className={`mt-2 w-full text-xs py-2 rounded-md border ${
                    selectedBait === null
                      ? "border-cyan-300 bg-cyan-900/40 text-cyan-100"
                      : "border-cyan-900/60 text-cyan-300/70 hover:text-cyan-100"
                  }`}
                  data-testid="bait-pick-none"
                >
                  Bare hook (100% trash roll)
                </button>
              </div>

              {/* Cast button */}
              <button
                onClick={cast}
                disabled={casting || (selectedBait && (s.bait[selectedBait] || 0) <= 0)}
                data-testid="cast-btn"
                className="mt-4 w-full py-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase tracking-[0.25em] text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {casting ? "Casting…" : "🎣 Cast Line"}
              </button>
            </div>
          </div>
        )}

        {tab === "shack" && (
          <div
            className="rounded-2xl border border-amber-800/50 bg-gradient-to-br from-amber-950/25 to-black p-5"
            data-testid="oasis-shack"
          >
            <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400">
              Sun-Cured Tackle Shack
            </div>
            <h2 className="text-xl font-black text-amber-100 mt-0.5" style={{ fontFamily: "Anton, sans-serif" }}>
              BAIT & TRADE
            </h2>
            <p className="text-[11px] text-amber-100/60 italic mt-1">
              Rustic driftwood counter. No registration fees. No time limits.
            </p>

            <div className="mt-4 space-y-2">
              {Object.values(bait).map((b) => (
                <div
                  key={b.id}
                  className="flex items-center gap-3 rounded-lg border border-amber-900/50 bg-black/40 px-3 py-2.5"
                >
                  <span className="text-3xl">{b.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-amber-100">{b.name}</div>
                    <div className="text-[10px] text-amber-100/60">{b.blurb}</div>
                  </div>
                  <button
                    onClick={() => buyBait(b.id)}
                    disabled={chits < b.price_chits}
                    data-testid={`shack-buy-${b.id}`}
                    className="shrink-0 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-500 text-black font-black text-xs uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Buy — {b.price_chits}🪙
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "bag" && (
          <div className="space-y-5">
            {/* Fish */}
            <div className="rounded-2xl border border-cyan-800/50 bg-black/40 p-5" data-testid="bag-fish">
              <div className="text-[10px] uppercase tracking-[0.35em] text-cyan-400">Fish</div>
              <h3 className="text-lg font-black text-cyan-100 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
                FRESH CATCH
              </h3>
              <div className="space-y-2">
                {Object.values(fish).map((f) => {
                  const n = s.fish_bag[f.id] || 0;
                  return (
                    <div key={f.id} className="flex items-center gap-3 rounded-lg border border-cyan-900/40 bg-black/40 px-3 py-2.5">
                      <span className="text-3xl">{f.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-cyan-100">{f.name} · <span className="text-cyan-300 text-xs">×{n}</span></div>
                        <div className="text-[10px] text-cyan-200/60">{f.blurb}</div>
                      </div>
                      <button
                        onClick={() => sellFish(f.id)}
                        disabled={n <= 0}
                        data-testid={`bag-sell-fish-${f.id}`}
                        className="shrink-0 px-3 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 text-black font-black text-xs uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Sell — {f.sell_chits}🪙
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Trash */}
            <div className="rounded-2xl border border-neutral-800 bg-black/40 p-5" data-testid="bag-trash">
              <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-400">Debris</div>
              <h3 className="text-lg font-black text-neutral-100 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
                CANYON CAST-OFFS
              </h3>
              <div className="space-y-2">
                {Object.values(trash).map((t) => {
                  const n = s.trash_bag[t.id] || 0;
                  const gift = t.gift_to;
                  return (
                    <div key={t.id} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-neutral-800 bg-black/40 px-3 py-2.5">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-3xl">{t.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-black text-neutral-100">{t.name} · <span className="text-neutral-400 text-xs">×{n}</span></div>
                          <div className="text-[10px] text-neutral-400 leading-snug">{t.blurb}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:shrink-0">
                        <button
                          onClick={() => sellTrash(t.id)}
                          disabled={n <= 0}
                          data-testid={`bag-sell-trash-${t.id}`}
                          className="px-2.5 py-1.5 rounded-md bg-neutral-700 hover:bg-neutral-600 text-white text-[11px] font-black uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Sell — 1🪙
                        </button>
                        {gift && (
                          <button
                            onClick={() => giftTrash(t.id)}
                            disabled={n <= 0}
                            data-testid={`bag-gift-${t.id}`}
                            className="px-2.5 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-[11px] font-black uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Gift → {gift === "cari" ? "🐕 Cari" : "🍳 David"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Flavor stacks */}
              <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
                <span className="px-2 py-1 rounded-md bg-emerald-950/40 border border-emerald-800/50 text-emerald-200">
                  🐕 Cari's Clamshell Stack: {s.cari_clamshells || 0}
                </span>
                <span className="px-2 py-1 rounded-md bg-emerald-950/40 border border-emerald-800/50 text-emerald-200">
                  🍳 David's Rag Pile: {s.david_rag_pile || 0}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
